import { Injectable } from "@angular/core";
import { createFrom, EventService, ProductService } from "@spartacus/core";
import { combineLatest, filter, map, Observable, of, switchMap, take } from "rxjs";
import { UserAccountFacade } from "@spartacus/user/account/root";
import { GonvarriUser } from "src/app/core/models/gonvarri-user.model";
import { GonvarriProductDetailsPageEvent, GonvarriSearchPageResultsEvent } from "./gonvarri-product-page.events";
import { ProductDetailsPageEvent, SearchPageResultsEvent } from "@spartacus/storefront";


@Injectable({ providedIn: 'root' })
export class GonvarriProductPageEventBuilder {

  constructor(
      protected event: EventService,
      protected userAccountFacade: UserAccountFacade,
      protected productService: ProductService,
  ) {
    this.register();
  }

  protected register(): void {
    this.event.register(
      GonvarriProductDetailsPageEvent,
      this.buildProductDetailsPageEvent()
    );
    this.event.register(
      GonvarriSearchPageResultsEvent,
      this.buildSearchPageResultsEvent()
    );
  }

  protected buildProductDetailsPageEvent(): Observable<GonvarriProductDetailsPageEvent> {
    return combineLatest([
      this.event.get(ProductDetailsPageEvent), // Get the ProductDetailsPageEvent to get the product code
      this.userAccountFacade.get().pipe(
        filter(user => !!user),
        take(1)
      ) // Get the user details
    ]).pipe(
      switchMap(([productDetailsEvent, user]) => {
        return this.productService.get(productDetailsEvent?.code!).pipe( // Get the product details using the product code
          map(product => {
            return createFrom(GonvarriProductDetailsPageEvent, {
              // Here we are mapping the product details and user details to the custom event matching the same GTM tag used by accelerator. 
              product_category: productDetailsEvent?.categories?.[0]?.name?.split("-")[1]?.trimStart() ?? '',
              product_id: productDetailsEvent?.code,
              product_name: product?.name,
              product_price: product?.price?.value,
              user_id: (user as GonvarriUser).orgUnit?.uid ?? '',
              user_email: user?.uid ?? '',
              product_stock: product?.stock?.stockLevel?.toString() ? `${product?.stock?.stockLevel?.toString()}Kg` : '0Kg'
            });
          })
        )
      }),
    );
  }

  protected buildSearchPageResultsEvent(): Observable<GonvarriSearchPageResultsEvent> {
    return combineLatest([
      this.event.get(SearchPageResultsEvent), // Get the SearchPageResultsEvent to get the search text
      this.userAccountFacade.get().pipe(
        filter(user => !!user),
        take(1)
      ) // Get the user details
    ]).pipe(
      switchMap(([searchPageResultsEvent, user]) => {
        return of(createFrom(GonvarriSearchPageResultsEvent, {
          // Here we are mapping the search text and user details to the custom event matching the same GTM tag used by accelerator. 
          search_text: searchPageResultsEvent?.searchTerm,
          user_id: (user as GonvarriUser).orgUnit?.uid ?? '',
          user_email: user?.uid ?? '',
        }));
      })
    )
  }

}
